// Padelution tournament formats, mirrored from the padelution-tournament client.
//
// Source: padelution-tournament-js/src/formats.ts:45-127 (Americano) and
//         padelution-tournament-js/src/quick-tournaments.ts:9-13 (Quick Tournament).
// That package is a headless Node client (type: module, Node >= 20) that scrapes
// padelution.com over Livewire using a logged-in user session, so it cannot be
// imported here -- it has to run server side. The table is therefore duplicated
// rather than imported, and has to be re-checked when the client is updated.
// The client itself was verified against padelution.com on 2026-08-26.
//
// Note the client treats these counts as friendly pre-validation only: every create
// call re-validates against the <select> options the live server renders, so the
// site remains the source of truth.

const range = (from, to, step = 1) => {
  const values = []
  for (let n = from; n <= to; n += step) values.push(n)
  return values
}

export const QUICK_TOURNAMENT = 'quick-tournament'

// Bracket sizes in TEAMS (quick-tournaments.ts:9).
export const QUICK_TOURNAMENT_SIZES = [4, 8, 16, 32, 64]

export const PADELUTION_FORMATS = [
  {
    slug: 'americano',
    type: 1,
    label: 'Americano',
    playerCounts: range(4, 24),
    supportsPointsPerMatch: true,
    supportsBoxSeeding: false,
    fixedPairs: false,
    description: 'Parit vaihtuvat joka kierros, joten kaikki pelaavat kaikkien kanssa. Pisteet ovat henkilökohtaisia.',
  },
  {
    slug: 'team-americano',
    type: 2,
    label: 'Team Americano',
    playerCounts: range(6, 24, 2),
    supportsPointsPerMatch: true,
    supportsBoxSeeding: false,
    fixedPairs: true,
    description: 'Americano-kierrätys kiinteillä pareilla; peräkkäiset pelaajat muodostavat joukkueen.',
  },
  {
    slug: 'mexicano',
    type: 3,
    label: 'Mexicano',
    playerCounts: range(4, 40),
    supportsPointsPerMatch: true,
    supportsBoxSeeding: false,
    fixedPairs: false,
    description: 'Kunkin kierroksen parit arvotaan sen hetkisen tilanteen perusteella.',
  },
  {
    slug: 'team-mexicano',
    type: 4,
    label: 'Team Mexicano',
    playerCounts: [...range(6, 20, 2), 24, 28, 32, 36, 40],
    supportsPointsPerMatch: true,
    supportsBoxSeeding: false,
    fixedPairs: true,
    description: 'Tilanteeseen perustuva parinmuodostus kiinteillä pareilla.',
  },
  {
    slug: 'mixicano',
    type: 5,
    label: 'Mixicano',
    playerCounts: range(8, 24, 2),
    supportsPointsPerMatch: true,
    supportsBoxSeeding: false,
    fixedPairs: false,
    description:
      'Sekaparit: pelaajalistan alkupuolisko on ryhmä A, loppupuolisko ryhmä B, ja jokaiseen pariin tulee yksi kummastakin.',
  },
  {
    slug: 'up-and-down',
    type: 6,
    label: 'Up and Down',
    playerCounts: range(8, 48, 4),
    supportsPointsPerMatch: false,
    supportsBoxSeeding: false,
    fixedPairs: false,
    description: 'Voittajat siirtyvät kentän ylöspäin, häviäjät alaspäin. Kierroksia pelataan haluttu määrä.',
  },
  {
    slug: 'team-up-and-down',
    type: 7,
    label: 'Team Up and Down',
    playerCounts: range(8, 48, 4),
    supportsPointsPerMatch: false,
    supportsBoxSeeding: false,
    fixedPairs: true,
    description: 'Up and Down kiinteillä pareilla; peräkkäiset pelaajat muodostavat joukkueen.',
  },
  {
    slug: 'beat-the-box',
    type: 8,
    label: 'Beat the Box',
    playerCounts: [8, 12, 16],
    supportsPointsPerMatch: true,
    supportsBoxSeeding: true,
    fixedPairs: false,
    description: 'Ensin alkulohkot, sitten tuloksien perusteella seedatut jatkolohkot.',
  },
  {
    // A different family: knockout brackets, created through a separate entry point
    // (createQuickTournament) rather than createAmericano.
    slug: QUICK_TOURNAMENT,
    type: null,
    label: 'Quick Tournament (cup)',
    playerCounts: null, // even, >= 8, <= maxTeams * 2 -- see validatePlayerCount
    supportsPointsPerMatch: false,
    supportsBoxSeeding: false,
    fixedPairs: false,
    description: 'Cup-kaavio suoralla pudotuspelillä. Vaatii kirjautumisen Padelutioniin.',
  },
]

export const getPadelutionFormat = (slug) => PADELUTION_FORMATS.find((f) => f.slug === slug) || null

// Human-readable summary of the player counts a format accepts, for the dropdown.
export const playerCountHint = (slug) => {
  if (slug === QUICK_TOURNAMENT) return 'parillinen määrä, vähintään 8'
  const format = getPadelutionFormat(slug)
  if (!format || !format.playerCounts || format.playerCounts.length === 0) return ''
  const counts = format.playerCounts
  const min = counts[0]
  const max = counts[counts.length - 1]
  if (counts.length <= 3) return counts.join(', ')
  const step = counts[1] - counts[0]
  return step === 1 ? min + '-' + max : min + '-' + max + ', välein ' + step
}

// The client rejects a bad count outright (americano.ts:81, quick-tournaments.ts:79-94),
// so check before assembling a payload that could never succeed.
export const validatePlayerCount = (slug, count) => {
  if (!count || count <= 0) return { ok: false, reason: 'Aktiviteetilla ei ole osallistujia.' }
  if (slug === QUICK_TOURNAMENT) {
    if (count < 8) return { ok: false, reason: 'Quick Tournament vaatii vähintään 8 pelaajaa.' }
    if (count % 2 !== 0) return { ok: false, reason: 'Quick Tournament vaatii parillisen määrän pelaajia.' }
    const largest = QUICK_TOURNAMENT_SIZES[QUICK_TOURNAMENT_SIZES.length - 1] * 2
    if (count > largest) return { ok: false, reason: 'Quick Tournament tukee enintään ' + largest + ' pelaajaa.' }
    return { ok: true, reason: null }
  }
  const format = getPadelutionFormat(slug)
  if (!format) return { ok: false, reason: 'Tuntematon turnaustyyppi.' }
  if (format.playerCounts.includes(count)) return { ok: true, reason: null }
  return {
    ok: false,
    reason: format.label + ' hyväksyy pelaajamäärät: ' + playerCountHint(slug) + '. Osallistujia on ' + count + '.',
  }
}

const participantName = (participant) =>
  [participant.firstname, participant.lastname].filter((part) => part && String(part).trim()).join(' ').trim()

/**
 * Player names for the payload. The client requires every name to be non-empty and
 * UNIQUE (americano.ts:63,66 and quick-tournaments.ts:74-78) and throws otherwise, so
 * blanks are dropped and repeats are suffixed rather than sent as-is. Two participants
 * sharing a name is ordinary, and would otherwise fail the whole request.
 */
export const padelutionPlayerNames = (participants) => {
  const names = []
  const seen = {}
  let skipped = 0
  let deduped = 0
  ;(participants || []).forEach((participant) => {
    const base = participantName(participant)
    if (!base) {
      skipped += 1
      return
    }
    if (seen[base]) {
      seen[base] += 1
      deduped += 1
      names.push(base + ' (' + seen[base] + ')')
    } else {
      seen[base] = 1
      names.push(base)
    }
  })
  return { names, skipped, deduped }
}

/**
 * The exact object the client's createAmericano / createQuickTournament would take.
 * Neither accepts a date, time, duration, court or venue -- the activity's own
 * schedule does not travel with the tournament.
 */
export const buildPadelutionPayload = ({ slug, name, participants }) => {
  const { names, skipped, deduped } = padelutionPlayerNames(participants)
  const payload =
    slug === QUICK_TOURNAMENT
      ? { name: (name || '').trim(), players: names }
      : { format: slug, name: (name || '').trim(), players: names }
  return { payload, skipped, deduped, playerCount: names.length }
}
